/**
 * Phase 6 cross-cutting PR 1c-a — `<BottomTab>`.
 *
 * Bottom-fixed primary navigation for Mobile (≤600px) + Comfortable
 * (601–1023px). 5 items (Today · Train · Races · Team · Settings) —
 * matches the post-cutover team-design mockup `_shell.js` which
 * injects the same 5-item nav at both Mobile and Comfortable
 * viewports.
 *
 * Tier guarding: this component renders nothing when the viewport is
 * wider than 1023px (SideNav takes over from Default). The mount
 * point (App.tsx) renders it unconditionally; the component does its
 * own tier-check via `useMediaQuery`. This keeps the App.tsx wiring
 * tier-agnostic and the responsiveness encapsulated.
 *
 * Active-route detection uses `useLocation` + `startsWith` so that
 * `/v2/plan/week/3` highlights "Train" (its parent tab). Today /
 * Train / Races / Team / Settings each match their own pathname
 * prefix.
 *
 * Sourced from
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §3 (N9 state)
 *   - `.claude/references/phase-6-cross-cutting-design/mockups/cross-cutting.css` .cc-bottomtab
 *   - `.claude/references/phase-6-cross-cutting-design/OVERRIDES.md` D3 (locked labels)
 */

import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountView } from '../../hooks/useAccountView';
import { useCoachMode } from '../../hooks/useCoachMode';
import { useGuardianApprovals } from '../../hooks/useGuardianApprovals';
import { useGuardianMode } from '../../hooks/useGuardianMode';
import { useServerFlag } from '../../hooks/useServerFlag';
import type { AccountViewId } from '../../lib/accountView';
import { isNativeApp } from '../../lib/platform';
import { BOOT_CHROME_FREE } from '../../lib/nav/bootChromeFree';
import { tabsForView } from '../../lib/nav/tabSets';
import { Icon, type IconName } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './BottomTab.module.css';

/**
 * CD-ICONS-01 — the tab glyph slot. ONE level, one switch, every tab bar.
 *
 * ── SIZE (Addendum A1 §2) ─────────────────────────────────────────────────
 * 22px, not 24. The direction is amended to "nav/tab 22–24, optical": at 24 the
 * glyph crowds the 10px label inside the 64px bar. This is a SIZE choice, not a
 * restroke — the viewBox and the 1.5 stroke are untouched, which is the rule
 * that keeps one pennant the same pennant at 22 and at 48.
 *
 * ── THE ACTIVE TREATMENT (Addendum A1 §3) ─────────────────────────────────
 * `nav.active`'s dot-under REPLACES the `.active::before` 2px top accent bar.
 * The marine colour and weight 700 stay. The retirement is scoped to
 * `data-icons` on the `<nav>`, which only exists on the flag-ON path — so
 * flag-OFF keeps the bar, keeps the attribute-free DOM, and stays byte-identical.
 *
 * ✅ EVERY TAB IS NOW COVERED. `icon` stays optional because the TYPE has to
 * allow a gap, but as of Addendum A2 there is none: A1 closed `train`
 * (`nav.train`) and `run`/`runs` (`nav.runs`), and A2 closed the guardian pair
 * — `guardian-home` → `nav.guardian-home`, `approvals` → `nav.approvals`.
 *
 * The mixed bar this file used to describe is gone. With `icon_system_v1` on,
 * all three tab sets — athlete, coach, guardian — render ratified art and no
 * emoji at all.
 */

/** A1 §2 — the tab glyph size, named once so the three bars cannot drift. */
const TAB_GLYPH_SIZE = 22;
function TabGlyph({
  icon,
  emoji,
  active,
  iconsOn,
}: {
  icon?: IconName;
  emoji: string;
  active: boolean;
  iconsOn: boolean;
}): JSX.Element {
  // `nav.active` (README §Interactions) — the glyph is unchanged and a solid
  // dot is added under it in the label colour. Composed by the registry so
  // BottomTab and SideNav cannot drift apart on the treatment.
  if (iconsOn && icon) return <Icon name={icon} size={TAB_GLYPH_SIZE} active={active} />;
  return <>{emoji}</>;
}

/**
 * Locked nav items (D3 in OVERRIDES.md). Labels stripped of
 * possessives per Phase 6c. `Today` lock is enforced here too.
 */
const NAV_ITEMS: ReadonlyArray<{
  key: string;
  label: string;
  glyph: string;
  /** CD-ICONS-01 — the ratified glyph, when one exists for this tab. */
  icon?: IconName;
  to: string;
  /** Pathname prefix used for active-state detection. */
  match: (pathname: string) => boolean;
}> = [
  {
    key: 'today',
    label: 'Today',
    glyph: '📅',
    icon: 'nav.today',
    to: '/today',
    match: (p) => p === '/today' || p === '/',
  },
  {
    key: 'train',
    label: 'Train',
    glyph: '🏃',
    // A1 §1 — Track: hollow loop from above, solid start dot.
    icon: 'nav.train',
    to: '/plan',
    match: (p) => p.startsWith('/plan'),
  },
  {
    key: 'races',
    label: 'Races',
    glyph: '🏁',
    icon: 'nav.races',
    to: '/races',
    match: (p) => p.startsWith('/races'),
  },
  {
    key: 'team',
    label: 'Team',
    glyph: '👥',
    icon: 'nav.team',
    to: '/team',
    match: (p) => p.startsWith('/team'),
  },
  {
    key: 'settings',
    label: 'Settings',
    glyph: '⚙️',
    icon: 'nav.settings',
    to: '/settings',
    match: (p) => p.startsWith('/settings'),
  },
];

/**
 * Mobile arc Part B — the live-run tracker's tab.
 *  - NATIVE (Capacitor shell, native GPS engine): Run takes the Settings
 *    slot → Today · Train · Run · Races · Team (5; Settings → avatar menu).
 *    **The native 5-tab rule stays — do NOT add a 6th native tab.**
 *  - WEB (mobile /v2): /run is the READ-ONLY runs history + replay review
 *    (canTrack=false). Founder 2026-07-07 clarified the no-6th-tab rule is
 *    native-only and asked for a discoverable "Runs" entry on web →
 *    Today · Train · Runs · Races · Team · Settings (6; the grid sizes to
 *    the item count). Label is "Runs" (review) vs native "Run" (track).
 */
const RUN_ROUTE = { to: '/run', match: (p: string) => p === '/run' };
const NATIVE_NAV_ITEMS: typeof NAV_ITEMS = isNativeApp()
  ? [
      ...NAV_ITEMS.filter((i) => i.key !== 'settings').slice(0, 2),
      // A1 §1 — Stopwatch. The SAME glyph for the native live-run tab ("Run")
      // and the web history entry ("Runs"); only the LABEL differs, and labels
      // are not this lane's to touch.
      { key: 'run', label: 'Run', glyph: '⏱️', icon: 'nav.runs', ...RUN_ROUTE },
      ...NAV_ITEMS.filter((i) => i.key !== 'settings').slice(2),
    ]
  : [
      ...NAV_ITEMS.slice(0, 2), // Today · Train
      { key: 'run', label: 'Runs', glyph: '⏱️', icon: 'nav.runs', ...RUN_ROUTE },
      ...NAV_ITEMS.slice(2), // Races · Team · Settings
    ];

/**
 * Routes where the in-app bottom nav must NOT render even when a user
 * is signed in. These surfaces have their own chrome (marketing
 * landing) or own flow grammar (onboarding progress shell) that the
 * BottomTab would disrupt.
 *
 * Surfaced 2026-05-16 — signed-in mobile athletes visiting
 * `/v2/landing` (e.g., via the new "Sign out" → landing redirect, OR
 * via the 307 from `/`) saw the in-app tab bar bleed into the
 * marketing page. The landing should be a clean marketing surface for
 * both signed-out and signed-in users.
 *
 * BrowserRouter has `basename="/v2"`, so `location.pathname` is
 * already stripped — `/landing` not `/v2/landing`.
 */
// '/run' (mobile Part B) — the live-run screen is immersive: the tab bar
// sat ON TOP of its Start/End controls on device. The screen carries its
// own exit affordance back to /today.
// '/welcome' (mobile v1 D5) — the shell's signed-out entry; chrome-free
// like the landing (covers the sign-in beat before navigation lands).
// '/' (BOOT §1) — the native boot splash; a fast session restore used to
// paint the tab bar over the launch celebration. Shared via BOOT_CHROME_FREE
// with CoachFab so the two lists can't drift again.
// '/plan/first-look' (SP-3) — the first-plan takeover owns the full screen
// (its own decision bar + dismiss); the tab bar would compete with it.
// '/plan/draft' (B-3) — the draft-week takeover, same reason (its own sticky
// decision row + origin-aware back); the tab bar would compete with it.
const CHROME_FREE_PATHS: ReadonlyArray<string> = [...BOOT_CHROME_FREE, '/plan/first-look', '/plan/draft'];

/**
 * CG-4 (design N1) — the reduced 3-item nav for guardian-only accounts:
 * Home · Approvals · Settings. The athlete's five tabs mean nothing to
 * a guardian. Dual athlete+guardian accounts keep the athlete tabs
 * untouched (N2 — they reach the guardian world via the account menu).
 */
const GUARDIAN_NAV_ITEMS: ReadonlyArray<{
  key: string;
  label: string;
  glyph: string;
  /** CD-ICONS-01 — absent on `guardian-home` and `approvals`: no ratified
   *  glyph covers either, and this lane invents none. */
  icon?: IconName;
  to: string;
  match: (pathname: string) => boolean;
}> = [
  {
    key: 'guardian-home',
    label: 'Home',
    glyph: '🏠',
    // A2 — Home. Closes the last uncovered tab in the app.
    icon: 'nav.guardian-home',
    to: '/guardian',
    match: (p) => p === '/guardian' || p === '/',
  },
  {
    key: 'approvals',
    label: 'Approvals',
    glyph: '📥',
    // A2 — Inbox.
    icon: 'nav.approvals',
    to: '/approvals',
    match: (p) => p.startsWith('/approvals'),
  },
  {
    key: 'settings',
    label: 'Settings',
    glyph: '⚙️',
    icon: 'nav.settings',
    to: '/settings',
    match: (p) => p.startsWith('/settings'),
  },
];

/**
 * UAT-B4 — the reduced 3-item tab bar for coach-only accounts:
 * Team · Meets · Settings (Races re-pointed to `Meets`, same /races route).
 * Approvals stays reachable via the bell + the team "Needs you today" strip —
 * no new nav row. Athlete + dual accounts keep the untouched 5/6-tab nav.
 */
const COACH_NAV_ITEMS: ReadonlyArray<{
  key: string;
  label: string;
  glyph: string;
  icon?: IconName;
  to: string;
  match: (pathname: string) => boolean;
}> = [
  {
    key: 'team',
    label: 'Team',
    glyph: '👥',
    icon: 'nav.team',
    to: '/team',
    match: (p) => p.startsWith('/team'),
  },
  {
    key: 'meets',
    label: 'Meets',
    glyph: '🏁',
    // Same route, same 🏁, same pennant — `Meets` is `Races` relabelled for a
    // coach (UAT-B4), not a different destination.
    icon: 'nav.races',
    to: '/races',
    match: (p) => p.startsWith('/races'),
  },
  {
    key: 'settings',
    label: 'Settings',
    glyph: '⚙️',
    icon: 'nav.settings',
    to: '/settings',
    match: (p) => p.startsWith('/settings'),
  },
];

type TabItem = (typeof NAV_ITEMS)[number];

/**
 * W2-CHROME (CH-4) — the tab items of a chosen VIEW: the specs from
 * `lib/nav/tabSets.ts` (key, label, route) joined by `key` to the glyph and
 * active-route matcher the three bars above already define. The bar below
 * renders from this with `settings_2_0_v1` on, so the bar and
 * `tabsForView(view, native)` cannot drift; `BottomTab.views.test.tsx` renders
 * the real bar for each view and asserts exactly that.
 */
const PRESENTATION_SOURCES: ReadonlyArray<TabItem> = [
  ...NAV_ITEMS,
  ...GUARDIAN_NAV_ITEMS,
  ...COACH_NAV_ITEMS,
  ...NATIVE_NAV_ITEMS,
];
function itemsForView(view: AccountViewId): TabItem[] {
  return tabsForView(view, isNativeApp()).map((spec) => {
    const art = PRESENTATION_SOURCES.find((i) => i.key === spec.key);
    return {
      ...spec,
      glyph: art?.glyph ?? '',
      icon: art?.icon,
      match: art?.match ?? ((): boolean => false),
    };
  });
}

/**
 * The tab bar. With `settings_2_0_v1` off this is the original role-derived bar
 * (`BottomTabBody` with no `view`), mounted exactly as before. With it on the
 * bar follows the account's chosen VIEW (`useAccountView`), one deterministic
 * tab set per view (B392). The fork sits one component above the body so the
 * view hook, and the role read behind it, never run with the flag off.
 */
export function BottomTab(): JSX.Element | null {
  const settingsOn = useServerFlag('settings_2_0_v1');
  return settingsOn ? <BottomTabByView /> : <BottomTabBody />;
}

function BottomTabByView(): JSX.Element | null {
  const { view } = useAccountView();
  return <BottomTabBody view={view} />;
}

function BottomTabBody({ view }: { view?: AccountViewId }): JSX.Element | null {
  // Mobile + Comfortable. SideNav takes over from Default (>= 1024).
  // Comfortable was previously left without nav (the original
  // BottomTab cut at 600 and SideNav didn't kick in until 1024,
  // stranding iPad-portrait / narrow-laptop users with no global nav).
  // ENG-2 fix: extend BottomTab coverage to 1023. Design mockups for
  // Comfortable (601–1023) all inject the BottomTab via the shared
  // _shell.js (see `.claude/references/post-cutover-team-design/
  // mockups/_shell.js`), confirming this is the intended approach.
  const isBelowDesktop = useMediaQuery('(max-width: 1023px)');
  const { user } = useAuthContext();
  // CG-4 — guardian-only detection (cached per user id). Athlete and
  // dual accounts fall through to the untouched 5-tab nav.
  const { guardianOnly } = useGuardianMode();
  // CG-8 — the Team-tab Reviews badge (cached per user id; zero fetches
  // while the coach_home_v1 flag is off). UAT-B4 — coachOnly drives the
  // reduced Team · Meets · Settings tab bar.
  const { pendingReviews, coachOnly } = useCoachMode();
  // CD-ICONS-01 — read ONCE for the whole bar and passed down, so the athlete,
  // coach and guardian tab sets cannot end up on different sides of the flag.
  const iconsOn = useIconSystem();
  // W2-CHROME CH-8 — with train_2_0_v1 on (and the icon system on) the Train
  // tab's glyph is "Week" (`nav.week`, a calendar page with a rising line)
  // instead of `nav.train`. Literal flag id; the tab's name stays "Train".
  const trainWeek = useServerFlag('train_2_0_v1');
  const location = useLocation();

  // Hide when signed out (no app routes to navigate to) and at
  // Default+ tiers (SideNav takes over from >= 1024 px).
  if (!isBelowDesktop || !user) return null;

  // BrowserRouter has `basename="/v2"` (App.tsx), so location.pathname
  // already strips the basename — `/today`, `/races`, etc.
  const pathname = location.pathname;

  // Hide on marketing / auth-flow surfaces even when the user is
  // signed in. Landing is a marketing surface (signed-in users still
  // see it cleanly when they hit `/` post-cutover); onboarding owns
  // its own progress chrome and shouldn't offer side-exits.
  if (CHROME_FREE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return null;
  }

  // W2-CHROME — `view` is set only with settings_2_0_v1 on, and then the
  // chosen view (not the role booleans) picks the bar and supplies its items.
  const viewItems = view ? itemsForView(view) : undefined;
  if (view ? view === 'guardian' : guardianOnly) {
    return <GuardianBottomTab pathname={pathname} items={viewItems} />;
  }
  if (view ? view === 'coaching' : coachOnly) {
    const coachItems = viewItems ?? COACH_NAV_ITEMS;
    return (
      <nav
        className={styles.tab}
        aria-label="Primary navigation"
        // A1 §3 — scopes the retirement of `.active::before`. Present ONLY on
        // the flag-ON path, so the OFF DOM is attribute-for-attribute today's.
        data-icons={iconsOn || undefined}
        style={{ gridTemplateColumns: `repeat(${coachItems.length}, 1fr)` }}
      >
        {coachItems.map((item) => {
          const isActive = item.match(pathname);
          const showBadge = item.key === 'team' && pendingReviews > 0;
          return (
            <Link
              key={item.key}
              to={item.to}
              className={[styles.item, isActive ? styles.active : ''].filter(Boolean).join(' ')}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className={styles.glyphWrap}>
                <span className={styles.glyph} aria-hidden="true">
                  <TabGlyph
                    icon={item.icon}
                    emoji={item.glyph}
                    active={isActive}
                    iconsOn={iconsOn}
                  />
                </span>
                {showBadge && (
                  <span className={styles.badge} aria-label={`${pendingReviews} week${pendingReviews === 1 ? '' : 's'} to approve`}>
                    {pendingReviews}
                  </span>
                )}
              </span>
              <span className={styles.label}>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    );
  }

  const athleteItems = viewItems ?? NATIVE_NAV_ITEMS;
  return (
    <nav
      className={styles.tab}
      aria-label="Primary navigation"
      // A1 §3 — see the coach bar above.
      data-icons={iconsOn || undefined}
      // Native = 5 tabs, web = 6 (the Runs review entry). Size the grid to
      // the item count so the CSS repeat(5) default doesn't clip the 6th.
      style={{ gridTemplateColumns: `repeat(${athleteItems.length}, 1fr)` }}
    >
      {athleteItems.map((item) => {
        const isActive = item.match(pathname);
        // CG-8 — pending plan reviews surface on the Team tab.
        const showBadge = item.key === 'team' && pendingReviews > 0;
        return (
          <Link
            key={item.key}
            to={item.to}
            className={[styles.item, isActive ? styles.active : ''].filter(Boolean).join(' ')}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className={styles.glyphWrap}>
              <span className={styles.glyph} aria-hidden="true">
                <TabGlyph
                  icon={trainWeek && item.key === 'train' ? 'nav.week' : item.icon}
                  emoji={item.glyph}
                  active={isActive}
                  iconsOn={iconsOn}
                />
              </span>
              {showBadge && (
                <span className={styles.badge} aria-label={`${pendingReviews} week${pendingReviews === 1 ? '' : 's'} to approve`}>
                  {pendingReviews}
                </span>
              )}
            </span>
            <span className={styles.label}>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * The guardian-only 3-item tab bar. A separate component so the
 * pending-approvals badge fetch (useGuardianApprovals) only ever mounts
 * for guardian-only accounts — athletes never pay for it.
 */
function GuardianBottomTab({
  pathname,
  items = GUARDIAN_NAV_ITEMS,
}: {
  pathname: string;
  /** W2-CHROME: the guardian view's items from `tabsForView`; default is today's. */
  items?: ReadonlyArray<TabItem>;
}): JSX.Element {
  const { approvals } = useGuardianApprovals();
  const pending = approvals.length;
  // Its own read: this component mounts independently of <BottomTab>'s body.
  // ⚠️ Only `settings` in this set has a ratified glyph — `guardian-home` and
  // `approvals` do not, so the guardian bar is the most visibly mixed of the
  // three with the flag on. See TabGlyph.
  const iconsOn = useIconSystem();

  return (
    <nav
      className={[styles.tab, styles.guardianTab].join(' ')}
      aria-label="Primary navigation"
      // A1 §3 — see the coach bar above.
      data-icons={iconsOn || undefined}
    >
      {items.map((item) => {
        const isActive = item.match(pathname);
        const showBadge = item.key === 'approvals' && pending > 0;
        return (
          <Link
            key={item.key}
            to={item.to}
            className={[styles.item, isActive ? styles.active : ''].filter(Boolean).join(' ')}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className={styles.glyphWrap}>
              <span className={styles.glyph} aria-hidden="true">
                <TabGlyph
                  icon={item.icon}
                  emoji={item.glyph}
                  active={isActive}
                  iconsOn={iconsOn}
                />
              </span>
              {showBadge && (
                <span className={styles.badge} aria-label={`${pending} pending`}>
                  {pending}
                </span>
              )}
            </span>
            <span className={styles.label}>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
